import { useEffect, useRef } from 'react';
import { getWsUrl } from '../config/api';
import type { PricePoint } from '../types/trading';

interface UsePriceStreamOptions {
  connectionKey: string;
  symbol: string;
  onPriceUpdate?: (symbol: string, price: number, timestamp: number) => void;
  onPriceHistory?: (symbol: string, point: PricePoint) => void;
  onReconnect?: () => void;
}

export function usePriceStreamWebSocket(options: UsePriceStreamOptions) {
  const { connectionKey, symbol, onPriceUpdate, onPriceHistory, onReconnect } = options;

  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimerRef = useRef<NodeJS.Timeout | null>(null);
  const intentionalCloseRef = useRef(false);
  const hasConnectedOnceRef = useRef(false);

  // Store callbacks in refs to avoid reconnection on callback changes
  const callbacksRef = useRef({ onPriceUpdate, onPriceHistory, onReconnect });
  callbacksRef.current = { onPriceUpdate, onPriceHistory, onReconnect };

  useEffect(() => {
    // Mark that we're intentionally closing
    intentionalCloseRef.current = true;
    // Reset reconnect tracking for new symbol/connection
    hasConnectedOnceRef.current = false;

    // Close previous connection
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }

    if (!symbol || !connectionKey) {
      return;
    }

    // Reset intentional close flag for new connection
    intentionalCloseRef.current = false;

    const connect = () => {
      if (wsRef.current?.readyState === WebSocket.OPEN) return;
      if (intentionalCloseRef.current) return; // Don't reconnect if intentionally closed

      const wsUrl = getWsUrl(`/brokers/${connectionKey}/stream-prices?figis=${symbol}`);
      console.log('[PriceWS] Connecting to', wsUrl);
      const ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        console.log('[PriceWS] Connected, streaming prices for', symbol);
        // Call onReconnect only on reconnections (not initial connect)
        if (hasConnectedOnceRef.current) {
          console.log('[PriceWS] Reconnected, triggering gap fill');
          callbacksRef.current.onReconnect?.();
        }
        hasConnectedOnceRef.current = true;
      };

      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);
          if (msg.price && (msg.figi || msg.type === 'last_price')) {
            const figi = msg.figi || symbol;
            const timestamp = msg.timestamp || Date.now();
            const timeSec = Math.floor(timestamp / 1000);

            // Update realtime price
            callbacksRef.current.onPriceUpdate?.(figi, msg.price, timestamp);

            // Update price history for line chart
            callbacksRef.current.onPriceHistory?.(figi, {
              time: timeSec,
              value: msg.price,
            });
          }
        } catch (err) {
          console.error('[PriceWS] Parse error:', err);
        }
      };

      ws.onclose = () => {
        console.log('[PriceWS] Disconnected');
        wsRef.current = null;
        // Only auto-reconnect if NOT intentionally closed
        if (!intentionalCloseRef.current) {
          reconnectTimerRef.current = setTimeout(connect, 3000);
        }
      };

      ws.onerror = (err) => {
        console.error('[PriceWS] Error:', err);
      };

      wsRef.current = ws;
    };

    connect();

    return () => {
      intentionalCloseRef.current = true;
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
      if (wsRef.current) wsRef.current.close();
    };
  }, [symbol, connectionKey]);

  return {
    isConnected: wsRef.current?.readyState === WebSocket.OPEN,
  };
}
